import React, { useEffect, useRef, useState } from "react";
import { Fade } from "react-awesome-reveal";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

import "../styling.css";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import { isMobile, isMobileOrTablet } from "../../utils/appUtils";
import { useParams } from "react-router-dom";
import ImageWithFallback from "../../components/ImageWithFallback/ImageWithFallback";
import caseSpecsData from '../../data/caseSpecs.json';
import watchListData from '../../data/watch-list.json';
import watchPageContent from '../../data/watchPageContent.json';
import { COMPONENT_COLORS } from '../../utils/componentColors';
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import Title from "../../components/Title/Title";
import Collection from "../../components/Collection/Collection";
import { useLanguage, Language } from '../../context/LanguageContext';
import { getWatchTranslations, WatchTranslation } from '../../translations/watchPageTemplate';
import { useEditMode } from '../../context/EditModeContext';
import EditableText from '../../components/EditableText/EditableText';
import { usePageContent } from '../../hooks/usePageContent';

// Update the type guard to handle essence_desthetique
function isValidProductId(productId: string | undefined): productId is string {
  return !!productId && (productId in watchPageContent);
}

// Add type for watchPageContent keys
type WatchPageContentKey = keyof typeof watchPageContent;

// Updated interface to handle multilingual data
interface LocalizedText {
  en: string;
  de: string;
  fr: string;
  zh: string;
}

interface Movement {
  type: string;
  diameter: string;
  depth: string;
}

interface LocalizedMovement {
  en: Movement;
  de: Movement;
  fr: Movement;
  zh: Movement;
}

interface CaseSpecifications {
  name: string;
  imageFilename: string;
  displayFileName: string;
  waterproofness: LocalizedText;
  watchGlass: LocalizedText;
  material: LocalizedText;
  mechanicalMovement?: LocalizedMovement;
  quartzMovement?: LocalizedMovement;
}

interface CaseSlideProps {
  specs: CaseSpecifications;
}

interface NumberWords {
  'en': string[];
  'de': string[];
  'fr': string[];
  'zh': string[];
}

const numberWords: NumberWords = {
  'en': ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'],
  'de': ['null', 'ein', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn'],
  'fr': ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix'],
  'zh': ['零', '一', '兩', '三', '四', '五', '六', '七', '八', '九', '十']
};

const WatchPage: React.FC = () => {
  const { productId } = useParams();
  const imagePrefix = `watches_collections_${productId}`;
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const carouselRef = useRef<any>(null);
  const pageContent = productId ? watchPageContent[productId as keyof typeof watchPageContent] : null;
  const { language } = useLanguage();
  const { isEditMode } = useEditMode();
  const { content, loading } = usePageContent<WatchTranslation>(productId || '');

  // New state for collection items and loading
  const [collectionItems, setCollectionItems] = useState<any[]>([]);
  const [watchesLoading, setWatchesLoading] = useState(false);

  // Create default translations when watchPageTranslations is not available
  const getDefaultTranslation = (content: typeof watchPageContent[WatchPageContentKey]): WatchTranslation => ({
    header: {
      title: content.header.title,
      mainTitle: content.header.mainTitle
    },
    firstSection: {
      title: content.firstSection.title,
      description: content.firstSection.description
    },
    models: {
      singular: 'Model',
      plural: 'Models'
    },
    specs: {
      mechanicalMovement: 'Mechanical Movement',
      quartzMovement: 'Quartz Movement',
      diameter: 'Diameter',
      depth: 'Depth',
      waterproofness: 'Waterproofness',
      watchGlass: 'Watch Glass',
      material: 'Material',
      thickness: 'Thickness'
    },
    variations: 'Variations'
  });

  // Update translation handling
  const t = isValidProductId(productId) ? (
    getWatchTranslations(
      productId,
      language,
      getDefaultTranslation(watchPageContent[productId as WatchPageContentKey])
    )
  ) : null;

  // Reset carousel position when productId changes
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.goToSlide(0);
      setIsFirstSlide(true);
    }
    console.log("productId", productId);
  }, [productId]);

  // Fetch watch details for new watches.json format
  useEffect(() => {
    let isMounted = true;
    async function fetchWatches() {
      if (!productId) {
        setCollectionItems([]);
        return;
      }
      const watchIds: string[] = watchListData[productId as keyof typeof watchListData] || [];
      setWatchesLoading(true);
      try {
        const items = await Promise.all(
          watchIds.map(async (id) => {
            try {
              const mod = await import(`../../data/watches/${id}.json`);
              const data = mod.default || mod;
              return {
                id: data.id,
                name: data.model,
                description: data.description[language],
                image: `/images/${data.imageSrc}.png`
              };
            } catch (e) {
              // If a file is missing, skip it
              return null;
            }
          })
        );
        if (isMounted) {
          setCollectionItems(items.filter(Boolean));
        }
      } finally {
        if (isMounted) setWatchesLoading(false);
      }
    }
    fetchWatches();
    return () => { isMounted = false; };
  }, [productId, language]);

  const caseCarouselResponsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 1
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 1
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1
    }
  };

  const watchCarouselResponsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1
    }
  };

  // Helper function to convert numbers to words
  const numberToWord = (num: number) => {
    const lang = (language in numberWords ? language : 'en') as keyof NumberWords;
    return num >= 0 && num <= 10 ? numberWords[lang][num] : num.toString();
  };

  const CaseSlide: React.FC<CaseSlideProps> = ({ specs }) => {
    // Extract movement data based on current language
    const mechanicalMovementData = specs.mechanicalMovement?.[language as keyof LocalizedMovement];
    const quartzMovementData = specs.quartzMovement?.[language as keyof LocalizedMovement];
    // Get localized texts based on current language
    const waterproofnessText = specs.waterproofness[language as keyof LocalizedText] || specs.waterproofness.en;
    const watchGlassText = specs.watchGlass[language as keyof LocalizedText] || specs.watchGlass.en;
    const materialText = specs.material[language as keyof LocalizedText] || specs.material.en;

    return (
      <div className="px-4 md:px-8 lg:px-16 relative">
        <div className="flex flex-col md:flex-row justify-center items-start gap-8 md:gap-16">
          <div className="w-full md:w-2/5">
            <div className="items-center relative">
              <h3 className="text-lg md:text-xl font-medium italic mb-4 mr-0">{specs.name}</h3>
            </div>

            <div className="h-[370px] md:h-auto overflow-y-auto custom-scrollbar">
              <div className="space-y-4 md:space-y-6 pr-2">
                {mechanicalMovementData && (
                  <div className="bg-white/5 p-3 md:p-4 rounded-lg">
                    <p className="font-medium text-sm md:text-sm mb-2">{t?.specs.mechanicalMovement.toLowerCase()}</p>
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 md:gap-x-8 text-sm md:text-sm">
                      <p className="min-w-[60px] md:min-w-[70px] text-gray-600">{t?.specs.diameter.toLowerCase()}</p>
                      <p>{mechanicalMovementData.diameter}</p>
                      <p className="min-w-[60px] md:min-w-[70px] text-gray-600">{t?.specs.thickness.toLowerCase()}</p>
                      <p>{mechanicalMovementData.depth}</p>
                    </div>
                  </div>
                )}

                {quartzMovementData && (
                  <div className="bg-white/5 p-3 md:p-4 rounded-lg">
                    <p className="font-medium text-sm md:text-sm mb-2">{t?.specs.quartzMovement.toLowerCase()}</p>
                    <div className="grid grid-cols-[auto_1fr] gap-x-4 md:gap-x-8 text-sm md:text-sm">
                      <p className="min-w-[60px] md:min-w-[70px] text-gray-600">{t?.specs.diameter.toLowerCase()}</p>
                      <p>{quartzMovementData.diameter}</p>
                      <p className="min-w-[60px] md:min-w-[70px] text-gray-600">{t?.specs.thickness.toLowerCase()}</p>
                      <p>{quartzMovementData.depth}</p>
                    </div>
                  </div>
                )}

                <div className="bg-white/5 p-3 md:p-4 rounded-lg">
                  <p className="font-medium text-sm md:text-sm mb-2">{t?.specs.waterproofness.toLowerCase()}</p>
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 md:gap-x-8 text-sm md:text-sm">
                    <p className="min-w-[60px] md:min-w-[70px] text-gray-600">{t?.specs.depth}</p>
                    <p>{waterproofnessText}</p>
                  </div>
                </div>

                <div className="bg-white/5 p-3 md:p-4 rounded-lg">
                  <p className="font-medium text-sm md:text-sm mb-2">{t?.specs.watchGlass.toLowerCase()}</p>
                  <p className="text-sm md:text-sm">{watchGlassText}</p>
                </div>

                <div className="bg-white/5 p-3 md:p-4 rounded-lg">
                  <p className="font-medium text-sm md:text-sm mb-2">{t?.specs.material.toLowerCase()}</p>
                  <p className="text-sm md:text-sm">{materialText}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:w-3/5 max-w-[800px]">
            <ImageWithFallback
              src={`/images/${specs.imageFilename}`}
              alt={specs.name}
              className="w-full"
              fallbackSrc={`/logo192.png`}
            />
          </div>
        </div>
      </div>
    );
  };

  const CustomRightArrow = ({ onClick, ...rest }: { onClick?: () => void }) => {
    const carouselProps = rest as any;
    const totalItems = carouselProps?.carouselState?.totalItems || 0;

    if (totalItems <= 1) return null;

    return (
      <button
        onClick={onClick}
        className="absolute right-[0px] top-[30%] -translate-y-1/2 w-10 h-10 bg-gray-200 opacity-50 hover:opacity-80 rounded-full shadow-sm hover:shadow-md flex items-center justify-center z-10"
        aria-label="Next slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  };

  const CustomLeftArrow = ({ onClick, ...rest }: { onClick?: () => void }) => {
    const carouselProps = rest as any;
    const totalItems = carouselProps?.carouselState?.totalItems || 0;

    if (totalItems <= 1) return null;

    return (
      <button
        onClick={onClick}
        className={`absolute left-[0px] top-[30%] -translate-y-1/2 w-10 h-10 bg-gray-200 opacity-50 hover:opacity-80 rounded-full shadow-sm hover:shadow-md flex items-center justify-center z-10 transition-opacity duration-300 ${isFirstSlide ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        aria-label="Previous slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  };

  const renderContent = () => {
    if (!pageContent || !productId || !t) return null;

    const fadeDuration = 2000;
    const caseSpecs = caseSpecsData[productId as keyof typeof caseSpecsData] || [];
    const modelCount = caseSpecs.length;
    const modelCountWord = numberToWord(modelCount);

    return (
      <>
        {/* Story About Section */}
        <div className="py-4 md:py-8">
          {isMobile() ? (
            <div className="px-4 md:px-8 lg:px-24">
              <p className="text-base md:text-lg">{t.firstSection.description}</p>
            </div>
          ) : (
            <TitleWithDescription
              title={{
                firstLine: isEditMode ? (
                  <EditableText
                    contentKey={`${productId}.firstSection.title`}
                    defaultValue={t.firstSection.title}
                  />
                ) : t.firstSection.title,
                secondLine: ""
              }}
              description={isEditMode ? (
                <EditableText
                  contentKey={`${productId}.firstSection.description`}
                  defaultValue={t.firstSection.description}
                  multiline={true}
                />
              ) : t.firstSection.description}
              layout="row"
              className="px-4 md:px-8 lg:px-24"
            />
          )}
        </div>

        {/* Video Section */}
        <div key={`video-${productId}`} className="w-full relative aspect-video">
          <video
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src={`/video/${productId}.mp4`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Models Section */}
        <div className="bg-gradient-to-r from-[#636B7E] via-[#A9BCC7] via-[#A5B7C2] to-[#636B7E] py-12 md:py-24">
          <div className="container mx-auto">
            <div className="px-4 md:px-8 lg:px-24">
              <h2 className="text-3xl md:text-4xl font-sans font-extralight ml-16 md:ml-0">
                {language === 'zh' ? (
                  // Special formatting for Chinese: number + 種 + term
                  <>
                    {numberToWord(modelCount)}種
                    {isEditMode ? (
                      <EditableText
                        contentKey={`${productId}.models.plural`}
                        defaultValue={t.models.plural}
                      />
                    ) : t.models.plural}
                  </>
                ) : (
                  // Original formatting for other languages
                  <>
                    {modelCountWord.toUpperCase()} {modelCount === 1 ? 
                      (isEditMode ? 
                        <EditableText
                          contentKey={`${productId}.models.singular`}
                          defaultValue={t.models.singular}
                        /> 
                        : t.models.singular) 
                      : 
                      (isEditMode ? 
                        <EditableText
                          contentKey={`${productId}.models.plural`}
                          defaultValue={t.models.plural}
                        /> 
                        : t.models.plural)
                    }
                  </>
                )}
              </h2>
            </div>

            <div className="relative">
              {/* Cases Section */}
              <div className="mx-auto mt-8 md:mt-16 min-h-[450px] px-4 md:px-8 lg:px-24">
                <Carousel
                  ref={carouselRef}
                  responsive={caseCarouselResponsive}
                  infinite={false}
                  removeArrowOnDeviceType={[]}
                  autoPlay={false}
                  className="case-carousel"
                  customRightArrow={<CustomRightArrow />}
                  customLeftArrow={<CustomLeftArrow />}
                  itemClass="px-0"
                  showDots={false}
                  afterChange={(previousSlide, { currentSlide }) => {
                    setIsFirstSlide(currentSlide === 0);
                  }}
                >
                  {caseSpecs.map((specs, index) => (
                    <CaseSlide
                      key={index}
                      specs={specs}
                    />
                  ))}
                </Carousel>
              </div>
              {/* Models Display Section */}
              <div className="mx-auto px-4 md:px-20">
                <div className={`grid mt-8 pb-4 place-items-center gap-6 md:gap-12 ${caseSpecs.length === 1 ? 'grid-cols-1 max-w-sm' :
                  caseSpecs.length === 2 ? 'grid-cols-2 max-w-2xl' :
                    caseSpecs.length === 3 ? 'grid-cols-3 max-w-4xl' :
                      caseSpecs.length === 4 ? 'grid-cols-4 max-w-6xl' :
                        'grid-cols-3 lg:grid-cols-6 max-w-[1920px]'
                  } mx-auto`}>
                  {caseSpecs.map((spec, index) => (
                    <div
                      key={index}
                      className="text-center w-full cursor-pointer transition-opacity hover:opacity-80"
                      onClick={() => {
                        const carousel = document.querySelector('.case-carousel .react-multi-carousel-track');
                        if (carousel) {
                          (carousel as any).style.transform = `translate3d(-${index * 100}%, 0px, 0px)`;
                        }
                      }}
                    >
                      <div className={`relative mb-2 mx-auto aspect-[2/3] ${caseSpecs.length >= 5 ? 'max-h-[200px] md:max-h-[300px]' : 'max-h-[180px] md:max-h-[250px]'
                        }`}>
                        <ImageWithFallback
                          key={`${spec.displayFileName}-${index}`}
                          src={`/images/${spec.displayFileName}.png`}
                          alt={spec.name}
                          className="w-full h-full object-contain"
                          fallbackSrc={`/logo192.png`}
                        />
                      </div>
                      <p className="text-xs md:text-sm uppercase">{spec.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Collection Grid */}
        <div className="bg-white pt-12 pb-8 md:pt-48 md:pb-12">
          <div className="container mx-auto px-4 md:px-8 lg:px-24">
            <h2 className="text-3xl md:text-4xl font-sans uppercase font-extralight text-center mb-8">
              {isEditMode ? 
                <EditableText
                  contentKey={`${productId}.variations`}
                  defaultValue={t.variations}
                /> 
                : t.variations}
            </h2>
          </div>
        </div>
        <div className="pb-8 md:pb-16 bg-gradient-to-r from-[#636B7E] via-[#A9BCC7] via-[#A5B7C2] to-[#636B7E]">
          {watchesLoading ? (
            <div className="text-center py-8 text-lg text-gray-500">Loading...</div>
          ) : (
            <Collection
              items={collectionItems}
              collectionId={productId || ''}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <BackgroundCover
        backgroundHeight={isMobileOrTablet() ? (isMobile() ? 400 : 600) : undefined}
        imagePosition="center"
        backgroundImage={`/images/${imagePrefix}_background.jpg`}
        isWatchPage={true}
      >
        <div className="absolute top-1/2 left-[100px] -translate-y-1/2 z-10">
          {t && (
            <Title
              firstLine={isEditMode ? (
                <EditableText
                  contentKey={`${productId}.header.title`}
                  defaultValue={t.header.title}
                />
              ) : t.header.title}
              secondLine={isEditMode ? (
                <EditableText
                  contentKey={`${productId}.header.mainTitle`}
                  defaultValue={t.header.mainTitle}
                />
              ) : t.header.mainTitle}
              className="text-white hidden md:block"
              animate={true}
            />
          )}
        </div>
      </BackgroundCover>
      <div className="w-full">
        {/* Mobile Title - Only visible on small screens */}
        {t && (
          <div className="block md:hidden mx-auto px-4 py-4 md:py-8">
            <Title
              firstLine={isEditMode ? (
                <EditableText
                  contentKey={`${productId}.header.title`}
                  defaultValue={t.header.title}
                />
              ) : t.header.title}
              secondLine={isEditMode ? (
                <EditableText
                  contentKey={`${productId}.header.mainTitle`}
                  defaultValue={t.header.mainTitle}
                />
              ) : t.header.mainTitle}
              className=""
              animate={true}
            />
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
};

const styles = `
  .case-carousel .react-multi-carousel-track {
    min-height: 700px;
  }

  @media (max-width: 768px) {
    .case-carousel .react-multi-carousel-track {
      min-height: 800px;
    }
  }

  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }

  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }
`;

// Add the styles to the document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

export default WatchPage;
